import React, { ReactNode, useMemo } from "react";
import classnames from "classnames";
import capitalize from "lodash/capitalize";
import Link, { LinkProps } from "next/link";
import { useSelector } from "react-redux";

import currencyFormatter from "common/currencyFormatter";
import { Dispensary } from "custom-types/Dispensary";
import { MenuItem } from "custom-types/MenuItem";
import { MenuItemV2 } from "custom-types/MenuItemV2";
import { VariantWithDeliveryInfo } from "custom-types/Variant";
import { getDomainCountryCode } from "redux/selectors/config";
import { getDistance } from "utils/distance";
import { formatQuantity } from "utils/formatQuantity";
import { getMenuItemUrl } from "utils/menuItemUrl";

import AdFlag from "components/botanic/AdFlag";
import CardFlag from "components/botanic/Card/CardFlag";
import StarRating from "components/botanic/StarRating";
import Verified from "components/Icons/verified.svg";
import Image from "components/Image";
import SmartOfferCard from "components/SmartOfferCard";

type Props = {
  className?: string;
  handleCardClick?: React.MouseEventHandler<HTMLAnchorElement>;
  /**
   * Show featured flag
   * @default false
   */
  isFeatured?: boolean;
  menuItem: MenuItem | MenuItemV2 | VariantWithDeliveryInfo;
  /**
   * Use the next/Link component instead of a plain anchor tag
   * @default true
   */
  useNextLink?: boolean;
  nextLinkProps?: LinkProps;
  /**
   * Pass an element used for order specific actions like add to bag
   */
  children?: ReactNode;
  /**
   * Pass an element used for rendering fulfillment UI component
   */
  renderFulfillmentInfo?: (dispensary: Dispensary) => void;
  /**
   * Show dispensary information
   */
  showFulfillment?: boolean;
  /**
   * Show similar strain flag
   */
  similarStrain?: boolean;
};

const MenuItemCard: React.FC<Props> = ({
  children,
  className,
  handleCardClick = () => {},
  isFeatured = false,
  menuItem,
  menuItem: {
    brandName,
    cbdContent,
    cbdUnit,
    deal,
    dispensary,
    formattedThumbnailUrl,
    id,
    name,
    offers,
    onlineFulfillmentEnabled,
    price,
    product,
    productCategory,
    quantity,
    strain,
    thcContent,
    thcUnit,
    unit,
  },
  useNextLink,
  nextLinkProps,
  renderFulfillmentInfo = () => {},
  showFulfillment = false,
  similarStrain = false,
}) => {
  const {
    discountAmount,
    discountedPrice,
    active: dealActive,
    discountLabel,
  } = deal || {};

  const {
    logoUrl,
    pickupEnabled,
    deliveryEnabled,
    id: dispensaryId,
    distanceMi,
    name: dispensaryName,
  } = dispensary || {};

  const showDeal = dealActive && discountAmount && discountedPrice;
  const showOffers = !showDeal && (offers?.length || 0) > 0;

  const menuItemUrl = getMenuItemUrl(menuItem, id);

  // Strain category is a deprecated attribute, replaced by phenotype.
  // Unlike category, the phenotype attribute is nullable, so this check
  // determines if the attribute is present in the strain object,
  // otherwise falling back to the non-nullable category attribute for
  // backwards compatibility.
  const phenotype = strain?.phenotype ? strain.phenotype : strain?.category;

  const productTypeText =
    productCategory === "Flower" && phenotype
      ? `${phenotype} ${productCategory}`
      : productCategory;

  // Unit and price display
  const displayQuantityFormatted =
    unit &&
    quantity &&
    formatQuantity({
      size: quantity,
      unit,
    });
  const displayPrice = showDeal ? discountedPrice / 100 : price;

  const dispensaryImage =
    logoUrl ||
    "https://public.leafly.com/dispensary/static/placeholder-dispensary-photo.jpg";

  const dispensaryMethod = useMemo(() => {
    if (showFulfillment && dispensaryId) {
      if (onlineFulfillmentEnabled) {
        // Dispensary has pickup and delivery enabled
        if (pickupEnabled && deliveryEnabled) {
          return "Pickup or delivery ";
          // Dispensary only has delivery enabled
        } else if (deliveryEnabled) {
          return "Delivery only ";
        } else {
          // Dispensary only has pickup enabled
          return "Pickup ";
        }
      } else {
        return "In-store only ";
      }
    }
  }, [
    pickupEnabled,
    deliveryEnabled,
    dispensaryId,
    onlineFulfillmentEnabled,
    showFulfillment,
  ]);

  const countryCode = useSelector(getDomainCountryCode);

  const dispensaryDistance =
    showFulfillment &&
    distanceMi !== null &&
    distanceMi !== undefined &&
    `${getDistance(distanceMi, countryCode)} away`;

  return (
    <div
      className={classnames(
        "flex flex-col justify-between relative h-full w-full bg-white elevation-low rounded p-lg pt-xl overflow-y-hidden",
        className
      )}
      data-testid="menu-item-card"
    >
      <CardLink
        className="block h-full w-full"
        useNextLink={useNextLink}
        nextLinkProps={nextLinkProps}
        menuItemUrl={menuItemUrl}
        handleCardClick={handleCardClick}
      >
        <CardFlag
          isFeatured={isFeatured}
          menuItem={menuItem}
          similarStrain={similarStrain}
        />
        <AdFlag
          promoted={"promoted" in menuItem ? !!menuItem.promoted : false}
        />
        <div className="mx-auto mb-md" style={{ width: 112 }}>
          <Image
            src={formattedThumbnailUrl}
            alt={name}
            sizes={[112]}
            ratio="square"
            data-testid="menu-item-card__image"
          />
        </div>
        <div className="text-secondary text-xs leading-none">
          {capitalize(productTypeText)}
        </div>
        {name && (
          <h3 className="font-bold text-sm break-words mt-xs">{name}</h3>
        )}
        {brandName && (
          <div className="flex">
            <h4 className="font-normal text-xs">by {brandName}</h4>
            {("isBrandVerified" in menuItem
              ? !!menuItem.isBrandVerified
              : false) && (
              <span className="ml-xs" style={{ paddingTop: "3px" }}>
                <Verified width="14" height="14" className="text-verified" />
              </span>
            )}
          </div>
        )}
        {((!!thcContent && !!thcUnit) || (!!cbdContent && !!cbdUnit)) && (
          <div className="flex flex-row font-bold text-xs mt-xs">
            {!!thcContent && !!thcUnit && (
              <div className="mr-sm">
                THC {`${thcContent}${thcUnit === "percent" ? "%" : thcUnit}`}
              </div>
            )}
            {!!cbdContent && !!cbdUnit && (
              <div>
                CBD {`${cbdContent}${cbdUnit === "percent" ? "%" : cbdUnit}`}
              </div>
            )}
          </div>
        )}

        {!!product?.avgApprovedPublicReviewRating && (
          <StarRating
            rating={product.avgApprovedPublicReviewRating}
            ratingCount={product.countApprovedPublicReviews}
            showRating={true}
            starWidth={14}
            data-testid="menu-item-card__rating"
            className="mt-xs"
          />
        )}
      </CardLink>
      <div>
        <CardLink
          className={classnames("mt-sm", { "block mb-sm": !!children })}
          useNextLink={useNextLink}
          nextLinkProps={nextLinkProps}
          menuItemUrl={menuItemUrl}
          handleCardClick={handleCardClick}
        >
          <>
            <div className="font-bold text-xs">{displayQuantityFormatted}</div>

            <div
              className={classnames("font-bold text-lg", {
                "text-notification": showDeal,
              })}
              data-testid="menu-item-card__price"
            >
              {`${currencyFormatter(displayPrice, countryCode)}`}
            </div>

            {showDeal && (
              <div
                data-testid="discount-details"
                className="flex items-center text-xs mt-xs"
              >
                <div
                  className="bg-notification rounded text-white font-bold mr-sm"
                  style={{ padding: "2px 4px" }}
                >
                  {discountLabel}
                </div>
                <span>{`reg ${currencyFormatter(price, countryCode)}`}</span>
              </div>
            )}

            {showOffers && offers?.length && (
              <div className="mt-xs">
                <SmartOfferCard offers={offers} abridged />
              </div>
            )}

            {showFulfillment && (
              <div
                className="flex items-center mt-xs"
                data-testid="menu-item-card__fulfillment"
              >
                <div
                  className="flex-shrink-0"
                  style={{ height: "24px", width: "24px" }}
                >
                  <Image
                    src={dispensaryImage}
                    alt={dispensaryName}
                    sizes={[24]}
                    className="rounded"
                  />
                </div>

                <div className="flex flex-col truncate font-bold text-xs ml-xs">
                  <span
                    className={classnames("truncate", {
                      "font-normal": !onlineFulfillmentEnabled,
                      "text-charcoal": onlineFulfillmentEnabled,
                    })}
                  >
                    {dispensaryMethod}
                    {dispensaryDistance}
                  </span>

                  <div className="truncate">
                    <span className="font-normal">from&nbsp;</span>
                    {dispensaryName}
                  </div>
                </div>
              </div>
            )}

            {renderFulfillmentInfo(dispensary as Dispensary)}
          </>
        </CardLink>
        {children}
      </div>
    </div>
  );
};

type CardLinkProps = {
  children: ReactNode;
  className?: string;
  useNextLink?: boolean;
  nextLinkProps?: LinkProps;
  menuItemUrl: string;
  handleCardClick: React.MouseEventHandler<HTMLAnchorElement>;
};

const CardLink: React.FC<CardLinkProps> = ({
  children,
  className,
  useNextLink = true,
  nextLinkProps,
  menuItemUrl,
  handleCardClick,
}) => {
  const sharedProps = {
    className,
    "data-testid": "menu-item-card-link",
    onClick: handleCardClick,
  };

  return useNextLink ? (
    <Link href={menuItemUrl} passHref {...nextLinkProps} {...sharedProps}>
      {children}
    </Link>
  ) : (
    <a href={menuItemUrl} {...sharedProps}>
      {children}
    </a>
  );
};

export default MenuItemCard;
